import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, ArrowRight, MessageCircle, RotateCcw } from "lucide-react";
import { api, inr } from "@/lib/apiClient";
import { waLink, CATEGORY_LABELS } from "@/data/site";
import { Reveal } from "@/components/Reveal";
import useSeo from "@/lib/useSeo";

const TRAVELLERS = [
  { v: "couple", l: "Couple / Honeymoon" },
  { v: "family", l: "Family" },
  { v: "solo", l: "Solo" },
  { v: "group", l: "Group" },
  { v: "luxury", l: "Luxury" },
  { v: "b2b", l: "B2B / Agent" },
];
const CATEGORIES = [
  { v: "pilgrimage", l: "Pilgrimage" },
  { v: "himalayan", l: "Himalayan" },
  { v: "leisure", l: "Leisure" },
  { v: "heritage", l: "Heritage" },
  { v: "offbeat", l: "Offbeat" },
  { v: "beach", l: "Beach" },
];
const DURATIONS = [
  { v: 5, l: "Up to 5 days" },
  { v: 8, l: "Around a week" },
  { v: 12, l: "10–12 days" },
  { v: 20, l: "Longer / flexible" },
];

export default function Recommender() {
  useSeo({ title: "Plan My Journey", description: "Answer a few questions and VacanzaGo's itinerary planner will match journeys from our own inventory, then connect you to a travel expert." });
  const [form, setForm] = useState({ traveller_type: "", category: "", max_days: null, budget: null, query: "" });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [refine, setRefine] = useState("");

  const pick = (k, v) => setForm((f) => ({ ...f, [k]: f[k] === v ? (typeof v === "number" ? null : "") : v }));

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/recommend", form);
      setResult(data);
      setTimeout(() => document.getElementById("reco-results")?.scrollIntoView({ behavior: "smooth" }), 100);
    } finally {
      setBusy(false);
    }
  };

  const reset = () => { setResult(null); setForm({ traveller_type: "", category: "", max_days: null, budget: null, query: "" }); };

  const chip = (active) =>
    `rounded-full border px-4 py-2 text-sm transition-all ${active ? "bg-[#F49E1A] border-[#F49E1A] text-white" : "border-[#553204]/20 text-[#553204] hover:border-[#F49E1A]"}`;

  const waFor = (it) =>
    waLink(`Hi Vacanza Go, your planner suggested "${it.title}" (${it.nights}N/${it.days}D). I'd like to know more.${form.query ? `\n\nMy ask: ${form.query}` : ""}`);

  return (
    <div className="pt-28 pb-24 bg-[#FFF7EC] min-h-screen">
      <div className="max-w-5xl mx-auto px-4 sm:px-8 lg:px-12">
        <Reveal className="text-center max-w-2xl mx-auto mb-12">
          <Sparkles size={30} className="mx-auto text-[#F49E1A]" />
          <h1 className="font-display text-5xl sm:text-6xl font-medium tracking-tight mt-4 text-[#2A1808]">Plan my journey</h1>
          <p className="mt-4 text-lg text-[#553204]">Tell us a little, and we'll match journeys from our own inventory. It's a concierge, not a form.</p>
        </Reveal>

        <div className="rounded-md border border-[#553204]/12 bg-[#FFFBF5] p-6 sm:p-10 space-y-8">
          <div>
            <p className="eyebrow mb-3">Who's travelling?</p>
            <div className="flex flex-wrap gap-2.5">
              {TRAVELLERS.map((t) => (
                <button key={t.v} data-testid={`reco-traveller-${t.v}`} onClick={() => pick("traveller_type", t.v)} className={chip(form.traveller_type === t.v)}>{t.l}</button>
              ))}
            </div>
          </div>
          <div>
            <p className="eyebrow mb-3">What kind of experience?</p>
            <div className="flex flex-wrap gap-2.5">
              {CATEGORIES.map((t) => (
                <button key={t.v} data-testid={`reco-category-${t.v}`} onClick={() => pick("category", t.v)} className={chip(form.category === t.v)}>{t.l}</button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
            <div>
              <p className="eyebrow mb-3">How long?</p>
              <div className="flex flex-wrap gap-2.5">
                {DURATIONS.map((t) => (
                  <button key={t.v} data-testid={`reco-days-${t.v}`} onClick={() => pick("max_days", t.v)} className={chip(form.max_days === t.v)}>{t.l}</button>
                ))}
              </div>
            </div>
            <div>
              <p className="eyebrow mb-3">Budget per person (approx)</p>
              <div className="flex flex-wrap gap-2.5">
                {[10000, 15000, 20000, 30000].map((b) => (
                  <button key={b} data-testid={`reco-budget-${b}`} onClick={() => pick("budget", b)} className={chip(form.budget === b)}>up to {inr(b)}</button>
                ))}
              </div>
            </div>
          </div>
          <div>
            <p className="eyebrow mb-3">Anything specific? (free text)</p>
            <textarea
              data-testid="reco-query"
              value={form.query}
              onChange={(e) => setForm((f) => ({ ...f, query: e.target.value }))}
              rows={3}
              placeholder="e.g. snow in Kashmir for our honeymoon, or a short pilgrimage near Haridwar…"
              className="w-full rounded-md border border-[#553204]/15 bg-white px-4 py-3 text-sm outline-none focus:border-[#F49E1A]"
            />
          </div>
          <div className="flex items-center gap-4">
            <button data-testid="reco-submit" onClick={run} disabled={busy} className="inline-flex items-center gap-2 rounded-full btn-orange px-8 py-3.5 text-sm disabled:opacity-60">
              {busy ? "Matching…" : "Find my journey"} <ArrowRight size={16} />
            </button>
            {result && <button onClick={reset} className="inline-flex items-center gap-2 text-sm text-[#8A6B47]"><RotateCcw size={15} /> Start over</button>}
          </div>
        </div>

        {result && (
          <div id="reco-results" className="mt-14 scroll-mt-28">
            <div className="text-center max-w-2xl mx-auto mb-10">
              <p className="eyebrow">{result.status === "overlap" ? "A few great fits" : result.status === "no_match" ? "Let's craft one" : "Your match"}</p>
              <h2 className="font-display text-3xl sm:text-4xl font-medium mt-2 text-[#2A1808]">{result.message}</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {result.matches.map((it) => (
                <div key={it.slug} data-testid={`reco-match-${it.slug}`} className="rounded-md border border-[#F49E1A]/40 bg-[#FFFBF5] overflow-hidden flex flex-col">
                  <div className="img-zoom aspect-[3/2] overflow-hidden">
                    <img src={it.hero_image} alt={it.title} className="h-full w-full object-cover" loading="lazy" />
                  </div>
                  <div className="p-6 flex-1 flex flex-col">
                    <p className="eyebrow">{CATEGORY_LABELS[it.category] || it.category} · {it.region}</p>
                    <h3 className="font-display text-2xl mt-1 text-[#2A1808]">{it.title}</h3>
                    {it._match_reasons?.length > 0 && (
                      <ul className="mt-3 space-y-1">
                        {it._match_reasons.slice(0, 3).map((r, i) => (
                          <li key={`${r}-${i}`} className="text-xs text-[#8A6B47] flex items-center gap-1.5"><span className="h-1 w-1 rounded-full bg-[#F49E1A]" /> {r}</li>
                        ))}
                      </ul>
                    )}
                    <div className="mt-auto pt-5 flex items-center justify-between">
                      <span className="font-display text-xl font-semibold text-[#2A1808]">from {inr(it.price_from)}</span>
                      <span className="text-xs text-[#8A6B47]">{it.nights}N/{it.days}D</span>
                    </div>
                    <div className="mt-4 flex gap-2">
                      <Link to={`/packages/${it.slug}`} className="flex-1 text-center rounded-full border border-[#553204]/20 px-4 py-2.5 text-sm text-[#2A1808] hover:bg-[#553204]/5">View</Link>
                      <a href={waFor(it)} target="_blank" rel="noreferrer" data-testid={`reco-wa-${it.slug}`} className="flex-1 text-center inline-flex items-center justify-center gap-1.5 rounded-full btn-orange px-4 py-2.5 text-sm"><MessageCircle size={14} /> Enquire</a>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {result.matches[0]?.day_plan?.length > 0 && (
              <div className="mt-10 rounded-md border border-[#553204]/12 bg-[#FFFBF5] p-6 sm:p-8" data-testid="reco-structured">
                <p className="eyebrow mb-1">Sample itinerary · {result.matches[0].title}</p>
                <p className="text-xs text-[#8A6B47] mb-5">From our actual Vacanza Go tour — we'll tailor the dates and pace with you.</p>
                <div className="space-y-5">
                  {result.matches[0].day_plan.map((d) => (
                    <div key={d.day} data-testid={`reco-day-${d.day}`} className="border-l-2 border-[#F49E1A]/40 pl-4">
                      <p className="font-display text-lg text-[#2A1808]">DAY {d.day} — 📍 {d.title}</p>
                      <p className="mt-1 text-sm text-[#553204]">🏔️ {d.detail}</p>
                      <p className="mt-1 text-xs text-[#8A6B47]">🏨 Stay & 🍽️ meals per package inclusions · 🌙 Overnight stay</p>
                    </div>
                  ))}
                </div>
                <div className="mt-6">
                  <p className="eyebrow mb-2">Refine this journey (we'll remember it)</p>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <input data-testid="reco-refine" value={refine} onChange={(e) => setRefine(e.target.value)} placeholder="e.g. make day 3 more relaxed, add a night in Srinagar…" className="flex-1 rounded-md border border-[#553204]/15 bg-white px-4 py-2.5 text-sm outline-none focus:border-[#F49E1A]" />
                    <a href={waLink(`Hi Vacanza Go, about the "${result.matches[0].title}" journey${form.query ? ` (I searched: ${form.query})` : ""}. Please refine: ${refine || "(tell us your change)"}`)} target="_blank" rel="noreferrer" data-testid="reco-refine-wa" className="inline-flex items-center justify-center gap-2 rounded-md btn-orange px-5 py-2.5 text-sm"><MessageCircle size={15} /> Send to expert</a>
                  </div>
                </div>
              </div>
            )}

            {result.alternatives?.length > 0 && (
              <div className="mt-12">
                <p className="eyebrow mb-4">{result.status === "no_match" ? "Journeys our team loves" : "You might also like"}</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {result.alternatives.map((it) => (
                    <Link key={it.slug} to={`/packages/${it.slug}`} data-testid={`reco-alt-${it.slug}`} className="group flex gap-3 items-center rounded-md border border-[#553204]/10 bg-[#FFFBF5] p-3 hover:border-[#F49E1A]">
                      <img src={it.hero_image} alt={it.title} className="h-16 w-16 rounded object-cover" loading="lazy" />
                      <div>
                        <p className="font-display text-lg leading-tight text-[#2A1808]">{it.title}</p>
                        <p className="text-xs text-[#8A6B47]">from {inr(it.price_from)} · {it.days}D</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-12 text-center rounded-md bg-[#361E02] text-[#F9EFE1] p-8">
              <p className="font-display text-2xl text-white">Ready to lock it in?</p>
              <p className="mt-2 text-sm text-[#F9EFE1]/75">Our team will tailor and itemise your quote within one business day.</p>
              <a href={waLink(`Hi Vacanza Go, I used the journey planner${form.query ? ` and asked for: ${form.query}` : ""}. Please help me plan.`)} target="_blank" rel="noreferrer" data-testid="reco-wa-team" className="inline-flex items-center gap-2 mt-5 rounded-full btn-orange px-7 py-3 text-sm">
                <MessageCircle size={16} /> Talk to a travel expert
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
